import { useEffect, useState, useRef, useCallback } from "react";
import { Form, Table } from "react-bootstrap";
import AppPagination from "../../Components/AppPagination";
import {
    getConfirmedOrders,
} from "../../Services/orderService";

import { useReactToPrint } from "react-to-print";

import PrintFrontSlip from "../../Components/Print/PrintFrontSlip";
import PrintMoForm from "../../Components/Print/PrintMoForm";
import PrintSingleMOForm from "../../Components/Print/PrintSingleMoForm";
import PrintSingleSlip from "../../Components/Print/PrintSingleSlip";
import PrintLoadSheet from "../../Components/Print/PrintLoadSheet";

const PrintOrders = () => {
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const contentRef = useRef(null);

    const reactToPrintFn = useReactToPrint({
        content: () => contentRef.current,
    });

    const [filters, setFilters] = useState({
        from: "",
        to: "",
        fromTime: "",
        toTime: "",
        trackingId: "",
    });

    const itemsPerPage = 50;

    // ✅ Format Time
    const formatTime = (time, isEnd = false) => {
        if (!time) return isEnd ? "23:59:59" : "00:00:00";
        return time.length === 5 ? `${time}:00` : time;
    };

    const fetchOrders = useCallback(async () => {
        try {
            const params = {
                limit: 50,
                page,
                trackingId: filters.trackingId || undefined,
            };

            // ✅ DateTime build
            if (filters.from) {
                params.from = `${filters.from}T${formatTime(filters.fromTime)}`;
            }

            if (filters.to) {
                params.to = `${filters.to}T${formatTime(filters.toTime, true)}`;
            }

            // ✅ AUTO FIX (swap if wrong)
            if (params.from && params.to) {
                const fromDate = new Date(params.from);
                const toDate = new Date(params.to);

                if (fromDate > toDate) {
                    console.warn("Swapping invalid date range");

                    const temp = params.from;
                    params.from = params.to;
                    params.to = temp;
                }
            }

            console.log("Final Params:", params);

            const res = await getConfirmedOrders(params);

            if (res?.status === "success") {
                setOrders(res.data || []);
                setTotalPages(res.totalPages || 1);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error("Fetch Orders Error:", error);
            setOrders([]);
        }
    }, [page, filters]);

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 400);

        return () => clearTimeout(delay);
    }, [fetchOrders]);

    const handleDateChange = (e) => {
        const { name, value } = e.target;

        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));

        setPage(1);
    };

    const handleSearchChange = (e) => {
        setFilters((prev) => ({
            ...prev,
            trackingId: e.target.value,
        }));

        setPage(1);
    };

    return (
        <div className="container pb-5">
            <h4 className="mb-3">Print Orders</h4>

            {/* Filters */}
            <div className="row g-2 mb-3 justify-content-end align-items-end">
                <div className="col-auto">
                    <Form.Label>From Time</Form.Label>
                    <Form.Control
                        type="time"
                        name="fromTime"
                        value={filters.fromTime}
                        onChange={handleDateChange}
                    />
                </div>

                <div className="col-auto">
                    <Form.Label>To Time</Form.Label>
                    <Form.Control
                        type="time"
                        name="toTime"
                        value={filters.toTime}
                        onChange={handleDateChange}
                    />
                </div>

                <div className="col-auto">
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                        type="date"
                        name="from"
                        value={filters.from}
                        onChange={handleDateChange}
                    />
                </div>

                <div className="col-auto">
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                        type="date"
                        name="to"
                        value={filters.to}
                        onChange={handleDateChange}
                    />
                </div>

                <div className="col-auto">
                    <Form.Label>Tracking ID</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Search"
                        value={filters.trackingId}
                        onChange={handleSearchChange}
                    />
                </div>

                {/* Print Buttons */}
                <div className="col-auto mt-4">
                    <PrintFrontSlip orders={orders} />
                </div>

                <div className="col-auto mt-4">
                    <PrintMoForm orders={orders} />
                </div>

                <div className="col-auto mt-4">
                    <PrintLoadSheet
                        title="Load Sheet"
                        orders={orders}
                        itemsPerPage={itemsPerPage}
                        page={page}
                    />
                </div>
            </div>

            {/* Table */}
            <Table bordered responsive>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Order ID</th>
                        <th>VPP Number</th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>City</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Action</th>
                    </tr>
                </thead>

                <tbody>
                    {orders.length ? (
                        orders.map((o, i) => (
                            <tr key={o.id}>
                                <td>{i + 1 + (page - 1) * itemsPerPage}</td>
                                <td>{o.trackingId}</td>
                                <td>{o.VppNumber || "-"}</td>
                                <td>{o.customerName}</td>
                                <td>{o.phoneNumber}</td>
                                <td>{o.city}</td>
                                <td>{o.quantity}</td>
                                <td>{o.amount}</td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <PrintSingleSlip order={o} />
                                        <PrintSingleMOForm order={o} />
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="9" className="text-center">
                                No orders found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <AppPagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
            />
        </div>
    );
};

export default PrintOrders;